import {
    ApiException,
    Configuration,
    JSONWebToken,
    LoginResponse,
    MinUser,
    RefreshJSONWebToken,
    SendCode,
    SendEmail,
    UserApi,
    UserRegistration,
    Verify
} from 'hotdesk_typescript';
import { getUserLocale } from '../lib/language.service';
import { storeGet } from '../helpers/utils';
import { User } from 'hotdesk_typescript';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { OAuthProvider } from 'firebase/auth';
import { dispatch } from '../store/store';
import {
    setRefreshToken,
    setToken,
    setUserData
} from '../store/slices/auth.slice';
import { APIError } from '../common-classes/gdpr.service';
import { sha256 } from '../utils/decode';
import {
    firebaseAnalytics,
    fetchRemoteConfig,
    setItidaCustomSignalFromStore
} from '../common-classes/firebase.service';
import { RemoteConfigKeys } from '../enum/remote-keys';

/**
 * Thrown when a social login (Google/Apple) hits an email that is already
 * registered with a different source (e.g. a Hotdesk password account).
 * Carries the existing user so callers can route to the password screen.
 */
export class SocialLoginAccountConflictError extends Error {
    constructor(public existingUser: MinUser) {
        super('Account already registered with a password');
        this.name = 'SocialLoginAccountConflictError';
    }
}

/**
 * Thrown when the user dismisses the social login popup (closes it, cancels,
 * or denies permission) before authenticating. Not a real failure — callers
 * should abort quietly without surfacing an error toast.
 */
export class SocialLoginCancelledError extends Error {
    constructor() {
        super('Social login cancelled by user');
        this.name = 'SocialLoginCancelledError';
    }
}

/**
 * Firebase auth error codes emitted when the user dismisses the popup rather
 * than completing (or failing) the sign-in. These are user cancellations, not
 * errors to report.
 */
const FIREBASE_POPUP_CANCELLED_CODES = [
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled'
];

const isPopupCancelledError = (error: unknown): boolean =>
    FIREBASE_POPUP_CANCELLED_CODES.includes(
        (error as { code?: string })?.code ?? ''
    );

export class AuthService {
    userApi!: UserApi;
    user = storeGet<User>('user') as User | null;
    uuid = '';

    constructor(apiConfig: Configuration) {
        this.userApi = new UserApi(apiConfig);
    }

    async getUser(token: RefreshJSONWebToken) {
        try {
            return await this.userApi.userLoginRefreshTokenCreate(token);
        } catch (error) {
            throw error;
        }
    }

    async getUserbyEmail(email: string) {
        try {
            return await this.userApi.userRead(email);
        } catch (error) {
            await firebaseAnalytics('web_login_email_error', {
                error: JSON.stringify(error)
            });
            return null;
        }
    }

    /**
     * `onAuthed` — post-login hook for callers that can refresh the App
     * Router tree in place (e.g. AuthModalV4 passes `router.refresh`).
     * When provided, it replaces the legacy full `window.location.reload()`
     * in the branches that don't hard-navigate: server components re-render
     * with the fresh auth cookie (subscription-aware SSR props on the
     * booking/listing pages) while Redux and client state survive. Callers
     * that don't pass it (legacy auth popup) keep the reload behavior.
     * The ITIDA / `redirectto` / fallback branches still assign
     * `window.location.href` — a full navigation re-runs everything anyway.
     */
    async login(
        login: JSONWebToken,
        onAuthed?: () => void
    ): Promise<LoginResponse> {
        try {
            const response = await this.userApi.userLoginJwtCreate(login);
            if (response) {
                dispatch(setUserData(response.user));
                dispatch(setToken(response.token));
                dispatch(setRefreshToken(response.refresh));
                await setItidaCustomSignalFromStore();
                const urlParams =
                    typeof window !== 'undefined'
                        ? new URLSearchParams(window.location.search)
                        : null;
                const isItidaInUrl = urlParams?.get('itida') === 'true';

                const itidaRedirect =
                    typeof window !== 'undefined'
                        ? sessionStorage.getItem('itida_redirect') === 'true'
                        : false;

                const itidaSignup =
                    typeof window !== 'undefined'
                        ? sessionStorage.getItem('itida_signup') === 'true'
                        : false;

                const itidaTextFieldEnabled = (await fetchRemoteConfig(
                    RemoteConfigKeys.WEB_LINKED_ACCOUNTS_ITIDA_TEXT_FIELD,
                    'boolean'
                )) as boolean;
                                
                const envUrl = process.env.NEXT_PUBLIC_SELF_URL;
                const itidaEnabled = envUrl === 'https://www.hotdesk.com/'
                    ? itidaTextFieldEnabled
                    : true;

                if (itidaSignup && itidaEnabled) {
                    if (typeof window !== 'undefined') {
                        sessionStorage.removeItem('itida_signup');
                        sessionStorage.removeItem('itida_redirect');
                        sessionStorage.setItem('show_itida_toast', 'true');
                    }
                    window.location.href = '/workspaces';
                    return response;
                }
                if ((isItidaInUrl || itidaRedirect) && itidaEnabled) {
                    if (typeof window !== 'undefined') {
                        sessionStorage.removeItem('itida_redirect');
                    }
                    window.location.href =
                        '/my-account?tab=linked-accounts&itida=true';
                    return response;
                }

                if (
                    window.location.pathname.includes('workspaces') ||
                    window.location.pathname.includes('booking') ||
                    window.location.pathname.includes('bookingDetails') ||
                    window.location.pathname.includes('memberships')
                ) {
                    const redirect = localStorage.getItem('redirectto');
                    if (redirect && !response?.user?.meta?.itidaId && response?.user?.isEmailVerified && response?.user?.isPhoneVerified) {
                        window.location.href = redirect;
                        localStorage.removeItem('redirectto');
                    } else if (onAuthed) onAuthed();
                    else window.location.reload();
                }
                else if (window.location.pathname === '/') {
                    if (onAuthed) onAuthed();
                    else window.location.reload();
                }
                else {
                    window.location.href = '/workspaces';
                }
            }
            return response;
        } catch (error) {
            console.error('Error during login:', error);
            throw error;
        }
    }

    async loginWithApple(onAuthed?: () => void) {
        const Appleprovider = new OAuthProvider('apple.com');
        Appleprovider.addScope('email');
        Appleprovider.addScope('name');
        if (process.env.NEXT_PUBLIC_APPLE_CLIENT_ID) {
            Appleprovider.setCustomParameters({
                clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID
            });
        }
        const auth = getAuth();
        let userData: JSONWebToken = {
            username: '',
            password: ''
        };
        let user = null;
        let profileData = null;
        try {
            user = await signInWithPopup(auth, Appleprovider);
            profileData = user.user.providerData[0];
            if (user.user) {
                const userRegistration: UserRegistration = {
                    email: user.user.email as string,
                    firstName: user.user.displayName
                        ? user.user.displayName
                        : (user.user.email
                            ?.split('@')[0]
                            .replace(/[^a-zA-Z\s]/g, '') as string),
                    username: user.user.email as string,
                    lastName: '',
                    password: await sha256(
                        `${user.user.email}${profileData?.uid}`
                    ),
                    meta: {
                        registration_source: 'Apple'
                    }
                };
                userData = {
                    username: userRegistration.username,
                    password: userRegistration.password
                };
                await this.register(userRegistration);
                await this.login(
                    {
                        username: userData.username,
                        password: await sha256(
                            `${user.user.email}${profileData?.uid}`
                        )
                    },
                    onAuthed
                );
            }
        } catch (error) {
            if (isPopupCancelledError(error)) {
                // User closed/cancelled the Apple popup — abort quietly.
                throw new SocialLoginCancelledError();
            }
            const { body } = error as ApiException<APIError>;
            if (!!body?.username) {
                // Email already registered. The derived password below only
                // works for accounts created via Apple — a Hotdesk/password
                // account must sign in with its own password, so surface a
                // conflict the caller can route to the password screen.
                const existing = await this.getUserbyEmail(userData.username);
                if (
                    existing &&
                    existing.registrationSource?.toLowerCase() !== 'apple'
                ) {
                    throw new SocialLoginAccountConflictError(existing);
                }
                await this.login(
                    {
                        username: userData.username,
                        password: await sha256(
                            `${userData.username}${profileData?.uid}`
                        )
                    },
                    onAuthed
                );
            } else {
                throw error;
            }
        }
    }

    async loginWithGoogle(onAuthed?: () => void) {
        const Googleprovider = new GoogleAuthProvider();
        Googleprovider.setCustomParameters({
            prompt: 'select_account '
        });
        const auth = getAuth();
        let userData: JSONWebToken = {
            username: '',
            password: ''
        };
        let user = null;
        let profileData = null;
        try {
            user = await signInWithPopup(auth, Googleprovider);
            profileData = user.user.providerData[0];
            if (user.user) {
                const userRegistration: UserRegistration = {
                    email: user.user.email as string,
                    firstName: user.user?.displayName as string,
                    username: user.user?.email as string,
                    lastName: '',
                    password: await sha256(
                        `${user.user?.email}${profileData?.uid}`
                    ),
                    meta: {
                        registration_source: 'Google'
                    }
                };
                userData = {
                    username: userRegistration.username,
                    password: userRegistration.password
                };
                await this.register(userRegistration);
                await this.login(
                    {
                        username: userData.username,
                        password: await sha256(
                            `${user.user?.email}${profileData?.uid}`
                        )
                    },
                    onAuthed
                );
            }
        } catch (error) {
            if (isPopupCancelledError(error)) {
                // User closed/cancelled the Google popup — abort quietly.
                throw new SocialLoginCancelledError();
            }
            const { body } = error as ApiException<APIError>;
            if (!!body?.username) {
                // Email already registered. The derived password below only
                // works for accounts created via Google — a Hotdesk/password
                // account must sign in with its own password, so surface a
                // conflict the caller can route to the password screen.
                const existing = await this.getUserbyEmail(userData.username);
                if (
                    existing &&
                    existing.registrationSource?.toLowerCase() !== 'google'
                ) {
                    throw new SocialLoginAccountConflictError(existing);
                }
                await this.login(
                    {
                        username: userData.username,
                        password: await sha256(
                            `${userData.username}${profileData?.uid}`
                        )
                    },
                    onAuthed
                );
            } else {
                throw error;
            }
        }
    }

    async sendVarificationEmail(emailData: SendEmail): Promise<SendEmail> {
        try {
            return await this.userApi.userSendVerificationEmail(emailData);
        } catch (error) {
            throw error;
        }
    }

    async register(user: UserRegistration): Promise<UserRegistration> {
        const languageCode = await getUserLocale();
        try {
            return await this.userApi.userRegister({
                ...user,
                meta: {
                    ...user.meta,
                    preferredLanguage: languageCode
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async varifyPhoneNumber(userId: number, data: Verify): Promise<Verify> {
        try {
            return this.userApi.userVerifyPhone(userId, data);
        } catch (error) {
            throw error;
        }
    }

    async sendPhoneCode(user: SendCode): Promise<SendCode> {
        try {
            return await this.userApi.userSendPhoneCode(user);
        } catch (error) {
            throw error;
        }
    }

    getUserById(userId: number) {
        return this.userApi.userRead_1(userId);
    }
}
